import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
} from '@tanstack/react-query';
import { apiClient } from './client';
import type { ApiResponse, ApiError } from './types';

// 通用查询 hook
export function useApiQuery<T = any>(
  queryKey: QueryKey,
  url: string,
  options?: Omit<
    UseQueryOptions<ApiResponse<T>, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<ApiResponse<T>, ApiError>({
    queryKey,
    queryFn: () => apiClient.get<T>(url),
    staleTime: 0, // 数据立即过期
    gcTime: 0, // 5分钟后清理缓存
    retry: 1,
    refetchOnWindowFocus: false,
    ...options,
  });
}

// 通用变更 hook
export function useApiMutation<T = any, V = any>(
  mutationFn: (variables: V) => Promise<ApiResponse<T>>,
  options?: UseMutationOptions<ApiResponse<T>, ApiError, V>
) {
  return useMutation<ApiResponse<T>, ApiError, V>({
    mutationFn,
    ...options,
  });
}

// POST 请求 hook
export function useApiPost<T = any, V = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<T>, ApiError, V>
) {
  return useApiMutation<T, V>(
    (data: V) => apiClient.post<T>(url, data),
    options
  );
}

// PUT 请求 hook
export function useApiPut<T = any, V = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<T>, ApiError, V>
) {
  return useApiMutation<T, V>(
    (data: V) => apiClient.put<T>(url, data),
    options
  );
}

// DELETE 请求 hook
export function useApiDelete<T = any>(
  url: string,
  options?: UseMutationOptions<ApiResponse<T>, ApiError, void>
) {
  return useApiMutation<T, void>(() => apiClient.delete<T>(url), options);
}

// 分页查询 hook
export function useApiPaginatedQuery<T = any>(
  queryKey: QueryKey,
  url: string,
  params: { page: number; pageSize: number; [key: string]: any },
  options?: Omit<
    UseQueryOptions<ApiResponse<T>, ApiError>,
    'queryKey' | 'queryFn'
  >
) {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryUrl = `${url}?${searchParams.toString()}`;

  return useApiQuery<T>(
    [...(Array.isArray(queryKey) ? queryKey : [queryKey]), params],
    queryUrl,
    options
  );
}
